import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseQueryResult,
} from "@tanstack/react-query";
import type {
  NotificationPreferenceChannel,
  NotificationPreferenceEvent,
  NotificationPreferenceList,
} from "@playbookai/types";
import { retryTransientReadStatus } from "@playbookai/api-clients";
import { useAuthStore } from "../auth/auth-store.js";
import { notificationsApi } from "../api/notifications-client.js";

export function notificationPreferencesQueryKey(
  tenantId: string | null | undefined,
  userId: string | undefined,
): readonly unknown[] {
  return [
    "notifications",
    "preferences",
    tenantId ?? "none",
    userId ?? "anon",
  ] as const;
}

function retryPreferenceRead(failureCount: number, error: Error): boolean {
  const status = (error as { status?: unknown }).status;
  return retryTransientReadStatus(
    failureCount,
    typeof status === "number" ? status : undefined,
  );
}

export function useNotificationPreferencesQuery(): UseQueryResult<NotificationPreferenceList> {
  const tenantId = useAuthStore((state) => state.claims?.tenantId);
  const userId = useAuthStore((state) => state.claims?.userId);
  return useQuery({
    queryKey: notificationPreferencesQueryKey(tenantId, userId),
    queryFn: () => notificationsApi.listPreferences(),
    enabled: Boolean(tenantId && userId),
    retry: retryPreferenceRead,
    refetchOnReconnect: "always",
  });
}

interface PreferenceWrite {
  readonly typeName: string;
  readonly channel: NotificationPreferenceChannel;
  readonly enabled: boolean;
  readonly queryKey: readonly unknown[];
}

interface BulkPreferenceWrite {
  readonly channel: NotificationPreferenceChannel;
  readonly enabled: boolean;
  readonly typeNames: readonly string[];
  readonly queryKey: readonly unknown[];
}

function replaceEvent(
  current: NotificationPreferenceList | undefined,
  event: NotificationPreferenceEvent,
): NotificationPreferenceList | undefined {
  if (!current) return current;
  return {
    events: current.events.map((candidate) =>
      candidate.typeName === event.typeName ? event : candidate,
    ),
  };
}

export function useSetNotificationPreference(): {
  readonly setPreference: (
    typeName: string,
    channel: NotificationPreferenceChannel,
    enabled: boolean,
  ) => void;
  readonly isPending: boolean;
  readonly error: Error | null;
  readonly reset: () => void;
} {
  const queryClient = useQueryClient();
  const tenantId = useAuthStore((state) => state.claims?.tenantId);
  const userId = useAuthStore((state) => state.claims?.userId);
  const queryKey = notificationPreferencesQueryKey(tenantId, userId);
  const mutation = useMutation<
    NotificationPreferenceEvent,
    Error,
    PreferenceWrite
  >({
    mutationFn: ({ typeName, channel, enabled }) =>
      notificationsApi.setPreference(typeName, channel, enabled),
    onSuccess: (event, variables) => {
      queryClient.setQueryData<NotificationPreferenceList>(
        variables.queryKey,
        (current) => replaceEvent(current, event),
      );
    },
    onError: (_error, variables) => {
      void queryClient.invalidateQueries({ queryKey: variables.queryKey });
    },
  });

  return {
    setPreference: (typeName, channel, enabled) =>
      mutation.mutate({ typeName, channel, enabled, queryKey }),
    isPending: mutation.isPending,
    error: mutation.error,
    reset: mutation.reset,
  };
}

/**
 * Applies one channel choice to every switchable event currently exposed by
 * the registry. The notification service deliberately stores sparse, per-event
 * overrides rather than a second global-preference model, so this action writes
 * through the same audited endpoint in small batches and then reloads the
 * authoritative decisions. Required and unavailable channels never enter the
 * batch.
 */
export function useSetAllNotificationPreferences(): {
  readonly setAll: (
    events: readonly NotificationPreferenceEvent[],
    channel: NotificationPreferenceChannel,
    enabled: boolean,
  ) => void;
  readonly isPending: boolean;
  readonly pendingChannel: NotificationPreferenceChannel | null;
  readonly error: Error | null;
  readonly reset: () => void;
} {
  const queryClient = useQueryClient();
  const tenantId = useAuthStore((state) => state.claims?.tenantId);
  const userId = useAuthStore((state) => state.claims?.userId);
  const queryKey = notificationPreferencesQueryKey(tenantId, userId);
  const mutation = useMutation<
    readonly NotificationPreferenceEvent[],
    Error,
    BulkPreferenceWrite
  >({
    mutationFn: async ({ typeNames, channel, enabled }) => {
      const updated: NotificationPreferenceEvent[] = [];
      const failures: unknown[] = [];
      const batchSize = 6;
      for (let index = 0; index < typeNames.length; index += batchSize) {
        const settled = await Promise.allSettled(
          typeNames
            .slice(index, index + batchSize)
            .map((typeName) =>
              notificationsApi.setPreference(typeName, channel, enabled),
            ),
        );
        for (const result of settled) {
          if (result.status === "fulfilled") updated.push(result.value);
          else failures.push(result.reason);
        }
      }
      if (failures.length > 0) {
        throw new Error(
          "Some notification settings could not be updated. Refresh and try again.",
        );
      }
      return updated;
    },
    onSuccess: (updated, variables) => {
      const byType = new Map(updated.map((event) => [event.typeName, event]));
      queryClient.setQueryData<NotificationPreferenceList>(
        variables.queryKey,
        (current) =>
          current
            ? {
                events: current.events.map(
                  (event) => byType.get(event.typeName) ?? event,
                ),
              }
            : current,
      );
    },
    // A failed batch can still contain successful writes. Always reload so the
    // quick status never claims the old all-on/all-off state after a partial
    // result, and so tenant-policy changes made concurrently are reflected.
    onSettled: (_data, _error, variables) => {
      void queryClient.invalidateQueries({ queryKey: variables.queryKey });
    },
  });

  return {
    setAll: (events, channel, enabled) => {
      const typeNames = events.flatMap((event) => {
        const decision = event.channels.find(
          (candidate) => candidate.channel === channel,
        );
        return decision?.control === "switchable" ? [event.typeName] : [];
      });
      if (typeNames.length === 0) return;
      mutation.mutate({ typeNames, channel, enabled, queryKey });
    },
    isPending: mutation.isPending,
    pendingChannel: mutation.isPending
      ? (mutation.variables?.channel ?? null)
      : null,
    error: mutation.error,
    reset: mutation.reset,
  };
}
